import type { ContributorArcheAssetContentType } from '@/lib/api';

/** Human-readable byte counts for quota meters and asset cards. */
export function formatBytes(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(2)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(2)} KB`;
  return `${b} B`;
}

/**
 * `accept=` for the upload picker. The audio MIME aliases are listed because
 * browsers disagree on what a .wav or .mp3 is (`audio/x-wav`, `audio/mp3`);
 * the extensions cover the rest.
 */
export const ACCEPTED_ASSET_MIMES =
  'image/png,image/jpeg,image/webp,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/wave,.mp3,.wav';
export const MAX_ASSET_UPLOAD_BYTES = 50 * 1024 * 1024;
/**
 * Sound files are short effects only — mirrors MAX_AUDIO_ASSET_BYTES in
 * @fias/db-types (the server enforces it; this fails fast).
 */
export const MAX_AUDIO_ASSET_UPLOAD_BYTES = 1024 * 1024;

const CONTENT_TYPE_BY_EXTENSION: Readonly<Record<string, ContributorArcheAssetContentType>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
};

/**
 * The canonical content type to declare for a file, from its extension —
 * never `file.type`, which varies by browser and OS for audio (a .wav is
 * `audio/wav`, `audio/x-wav` or `audio/wave`). The server checks the bytes
 * against it. Null when the library does not accept the file.
 */
export function assetContentTypeForFile(
  file: Pick<File, 'name'>,
): ContributorArcheAssetContentType | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return CONTENT_TYPE_BY_EXTENSION[ext] ?? null;
}

export function isAudioContentType(contentType: string): boolean {
  return contentType.startsWith('audio/');
}
